import { DriverV2Output } from '@allocab/allocab-driver-service-types';
import { DriverOptionsOutput } from '@allocab/allocab-driver-shared-types';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Label } from '../../components/atomic/Label';
import { GradientButton } from '../../components/button/GradientButton';
import { SwitchSelectButton, SwitchSelectItem } from '../../components/button/SwitchSelectButton';
import { PhoneInputControlled } from '../../components/form/PhoneInputControlled';
import { usePatchDriverUser } from '../../hooks/usePatchDriverUser';
import { ToastManager } from '../../services/toast/utils';
import { t } from '../../services/translation/translation';

type FieldValues = {
  phoneNumber: string;
  phoneNumberCountry: string;
};

type Props = {
  driver: DriverV2Output;
};

const canSeePriceItems: SwitchSelectItem<DriverOptionsOutput['canSeePrice']>[] = [
  { value: true, render: () => <span>{t('form.trip-price.show-price')}</span> },
  { value: false, render: () => <span>{t('form.trip-price.dont-show-price')}</span> },
];

export const DriverForm = ({ driver }: Props) => {
  const methods = useForm<FieldValues>({
    defaultValues: {
      phoneNumberCountry: driver.phoneNumberCountry,
      phoneNumber: driver.phoneNumber,
    },
  });
  const { handleSubmit } = methods;
  const { mutate: patchDriverUser, isLoading } = usePatchDriverUser();
  const [canSeePrice, setCanSeePrice] = useState(driver.params.canSeePrice ? driver.params.canSeePrice : false);

  const onSubmit = handleSubmit(({ phoneNumber, phoneNumberCountry }) => {
    if (driver.userKey && driver.email) {
      patchDriverUser(
        {
          userKey: driver.userKey,
          firstName: driver.firstName,
          lastName: driver.lastName,
          phoneNumber: phoneNumber.replaceAll(' ', ''),
          phoneNumberCountry,
          email: driver.email,
          canSeePrice: canSeePrice,
        },
        {
          onSuccess: () => {
            ToastManager.show({ severity: 'success', message: t('driver-route.update-success') });
          },
        },
      );
    }
  });

  return (
    <div className="block min-w-full sm:min-w-0 p-6 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
      <h6 className="text-lg font-bold dark:text-white mb-2">{t('common.profile')}</h6>
      <div className="space-y-4">
        <div>
          <Label>{t('label.email')}</Label>
          <p className="font-normal text-gray-500 dark:text-gray-400">{driver.email}</p>
        </div>
        <div>
          <Label>{t('form.trip-price.title')}</Label>
          <div className="space-y-2">
            <SwitchSelectButton
              items={canSeePriceItems}
              value={canSeePrice}
              onValueChange={canSeePrice => setCanSeePrice(canSeePrice)}
            />
          </div>
        </div>
        <FormProvider {...methods}>
          <PhoneInputControlled
            name="phoneNumber"
            countryName="phoneNumberCountry"
            label={t('common.phone')}
            rules={{ required: t('form.validation.required') }}
          />

          <GradientButton
            text={t('common.update')}
            type="button"
            className="w-full"
            onClick={onSubmit}
            isLoading={isLoading}
          />
        </FormProvider>
      </div>
    </div>
  );
};
